import React, { useState, FormEvent, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../lib/auth/AuthContext';
import { useApi } from '../../lib/apiClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Input,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Skeleton,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
    Alert,
    AlertTitle,
    AlertDescription,
    Slider,
} from "@pagespeedify/ui";
import { toast } from "sonner";
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, AlertTriangleIcon, Bot, ArrowRight } from 'lucide-react';
import { MonitoringUsageResponse, ApiResponse, Task } from '../../types';
import { UserType } from '../../lib/auth/types';
import { validateThresholds, getIdealScoresForFormFactor } from '../../utils/validation';
import { getMetricsForPlan, metricInfo, getUserPlanType, calculateIdealScoreRange } from '../../utils/monitoring';
import { ImageOptimizationForm } from '../image-optimization/ImageOptimizationForm';

type TaskType = 'monitoring' | 'image_op' | 'agent';

interface CreateTaskFormProps {
    initialType?: TaskType;
    onClose?: () => void; // To hide the form from the parent page
    onSuccess?: (taskId: string, type: TaskType, extraData?: any) => void;
}

// --- Cost Display Component (for Monitoring) ---
interface CreditValidationResult {
    canAfford: boolean;
    breakdown: string;
    totalOverageCost: number;
    userCredits: number;
}

const CostInfoDisplay: React.FC<{ validationResult: CreditValidationResult | null }> = ({ validationResult }) => {
    if (!validationResult) return <Skeleton className="h-4 w-48 mt-4" />;

    return (
        <div className="pt-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2 mb-2">
                {validationResult.canAfford ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-red-600" />}
                <span className={`font-medium ${validationResult.canAfford ? 'text-green-700' : 'text-red-700'}`}>
                    {validationResult.canAfford ? 'Sufficient credits' : 'Insufficient credits'}
                </span>
            </div>
            <p className="mb-2">{validationResult.breakdown}</p>
            {validationResult.totalOverageCost > 0 && <p>Your balance: {validationResult.userCredits} credits</p>}
            {!validationResult.canAfford && (
                <p className="text-red-600 mt-2">
                    You need {Math.max(0, validationResult.totalOverageCost - validationResult.userCredits)} more credits.
                    <Link to="/credits" className="ml-1 underline hover:text-red-700">Add credits</Link>
                </p>
            )}
            {validationResult.canAfford && validationResult.userCredits === 0 && validationResult.totalOverageCost === 0 && (
                <p className="text-amber-600 mt-2">
                    You have 0 credits. Consider adding credits for future monitoring tasks.
                    <Link to="/credits" className="ml-1 underline hover:text-amber-700">Add credits</Link>
                </p>
            )}
        </div>
    );
};

export const CreateTaskForm: React.FC<CreateTaskFormProps> = ({ initialType = 'monitoring', onClose, onSuccess }) => {
    const { user, isLoading: authIsLoading } = useAuth();
    const navigate = useNavigate();
    const apiClient = useApi();
    const queryClient = useQueryClient();
    const lastAuthenticatedUidRef = useRef<string | null>(null);
    const [taskType, setTaskType] = useState<TaskType>(initialType);

    // State for all possible fields
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [urlToMonitor, setUrlToMonitor] = useState('');
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
    const [formFactor, setFormFactor] = useState<'mobile' | 'desktop'>('mobile');
    const [thresholds, setThresholds] = useState({
        lcp: 2500,
        cls: 0.1,
        tti: 3800,
        fcp: 1800,
        ttfb: 800,
        tbt: 200,
    });

    useEffect(() => {
        const currentUid = user?.uid ?? null;
        const previousUid = lastAuthenticatedUidRef.current;
        if (previousUid && previousUid !== currentUid) {
            setTaskType(initialType);
            setTitle('');
            setDescription('');
            setUrlToMonitor('');
            setSelectedMetrics([]);
            setFormFactor('mobile');
            setThresholds({
                lcp: 2500,
                cls: 0.1,
                tti: 3800,
                fcp: 1800,
                ttfb: 800,
                tbt: 200,
            });
        }
        lastAuthenticatedUidRef.current = currentUid;
    }, [initialType, user?.uid]);

    const getMetricsToShow = () => {
        const planType = getUserPlanType(
            monitoringUsage?.accessContext?.planFeatures,
            user?.user_type
        );
        return getMetricsForPlan(planType);
    };

    // Update thresholds when form factor changes to use appropriate ideal scores
    useEffect(() => {
        const newIdealScores = getIdealScoresForFormFactor(formFactor);
        setThresholds({
            lcp: newIdealScores.lcp,
            cls: newIdealScores.cls,
            tti: newIdealScores.tti,
            fcp: newIdealScores.fcp,
            ttfb: newIdealScores.ttfb,
            tbt: newIdealScores.tbt,
        });
    }, [formFactor]);

    const handleThresholdChange = useCallback((metric: keyof typeof thresholds, value: string | number[]) => {
        const info = metricInfo[metric];
        const numericValue = Array.isArray(value) ? value[0] : Number(value);
        const storedValue = numericValue * info.conversion;
        setThresholds(prev => ({ ...prev, [metric]: storedValue }));
    }, []);

    const handleUrlBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
        const url = event.target.value;
        if (url) {
            try {
                const fullUrl = /^https?:\/\//.test(url) ? url : `https://${url}`;
                const response = await fetch(fullUrl);
                const text = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'text/html');
                const fetchedTitle = doc.querySelector('title')?.innerText || '';
                const metaDescription = doc.querySelector('meta[name="description"]');
                const fetchedDescription = metaDescription?.getAttribute('content') || '';
                setTitle(fetchedTitle || url);
                setDescription(fetchedDescription || '');
            } catch (error) {
                console.error('Failed to fetch website metadata (CORS might be the issue):', error);
                setTitle(url);
                setDescription('');
            }
        }
    };

    const isTeamOwner = user ? (!user.team_id || user.internalUserId === user.team_owner_id) : false;
    const normalizedUserType = (user?.user_type || '').toLowerCase();
    const isFreeLikeUser = !user || normalizedUserType === UserType.UNSUBSCRIBED || normalizedUserType === 'free';
    const isFreeUser = isFreeLikeUser;

    const { data: monitoringUsage, isLoading: isUsageLoading } = useQuery<MonitoringUsageResponse, Error>({
        queryKey: ['monitoringUsage', user?.uid],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            const response = await apiClient.get<{ success: boolean, usage: MonitoringUsageResponse }>('/monitoring/usage');
            if (!response.success || !response.usage) throw new Error('Failed to fetch monitoring usage data.');
            return response.usage;
        },
        enabled: !!user && !authIsLoading && !!apiClient && taskType === 'monitoring' && !isFreeUser,
    });

    // Image optimization usage query to check canCreate
    interface ImageUsageResponse {
        usage: {
            canCreate: {
                imageOptimizations: boolean;
                imageOptimizationsReason?: string;
            };
        };
    }

    const { data: imageOptUsageData } = useQuery<ImageUsageResponse, Error>({
        queryKey: ['image-optimization-usage-form', user?.uid || 'anonymous'],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            return await apiClient.get<ImageUsageResponse>('/images/usage');
        },
        enabled: !!user && !authIsLoading && !!apiClient,
        staleTime: 30000,
        retry: 1,
    });

    // Check if user can create image optimizations (default to false for safety)
    const canCreateImageOpt = imageOptUsageData?.usage?.canCreate?.imageOptimizations ?? false;

    // Normalize URL for duplicate comparison: lowercase, strip trailing slashes, strip www.
    const normalizeMonitoringUrl = (url: string) =>
        url.trim().toLowerCase().replace(/\/+$/, '').replace(/^(https?:\/\/)www\./, '$1');

    // Check if current URL + formFactor combination already exists
    const isDuplicateUrl = useMemo(() => {
        if (!monitoringUsage?.canCreate?.existingMonitoredUrls || !urlToMonitor.trim()) {
            return false;
        }
        const normalizedUrl = normalizeMonitoringUrl(urlToMonitor);
        return monitoringUsage.canCreate.existingMonitoredUrls.some(
            existing => normalizeMonitoringUrl(existing.url) === normalizedUrl && existing.formFactor === formFactor
        );
    }, [monitoringUsage?.canCreate?.existingMonitoredUrls, urlToMonitor, formFactor]);

    // Calculate credit validation for monitoring tasks
    const creditValidation = useMemo<CreditValidationResult | null>(() => {
        if (!monitoringUsage || taskType !== 'monitoring') return null;

        let totalOverageCost = 0;
        const breakdownParts: string[] = [];

        // URL overage cost
        if ((monitoringUsage.remainingInBundle ?? 0) <= 0) {
            const urlCost = monitoringUsage.overageCostPerUnit || 0;
            totalOverageCost += urlCost;
            breakdownParts.push(`1 URL: ${urlCost} credits (overage)`);
        } else {
            breakdownParts.push('1 URL: Included in plan');
        }

        // Metrics overage cost
        const metricsLimit = monitoringUsage.metricsUsage?.limitForPeriod || 0;
        const metricsOverageCost = monitoringUsage.metricsUsage?.overageCostPerUnit || 0;
        const extraMetricsCount = Math.max(0, selectedMetrics.length - metricsLimit);

        if (extraMetricsCount > 0) {
            const metricsCost = extraMetricsCount * metricsOverageCost;
            totalOverageCost += metricsCost;
            breakdownParts.push(`${extraMetricsCount} extra metric(s): ${metricsCost} credits`);
        } else if (selectedMetrics.length > 0) {
            breakdownParts.push(`${selectedMetrics.length} metric(s): Included in plan`);
        }

        const userCredits = user?.credits || 0;
        const canAfford = userCredits >= totalOverageCost;
        const breakdown = breakdownParts.join(' + ');

        return {
            canAfford,
            breakdown: totalOverageCost > 0 ? `${breakdown} = ${totalOverageCost} credits total` : breakdown,
            totalOverageCost,
            userCredits,
        };
    }, [monitoringUsage, selectedMetrics, user?.credits, taskType]);

    const mutation = useMutation<ApiResponse<Task | any>, Error, any>({
        mutationFn: (payload: any) => {
            return apiClient.post('/monitoring', payload);
        },
        onSuccess: (response) => {
            if (response.success) {
                toast.success(`Task "${urlToMonitor}" created successfully!`);
                queryClient.invalidateQueries({ queryKey: ['monitoredUrls'] });
                queryClient.invalidateQueries({ queryKey: ['ai-tasks-list'] });

                const taskId = response.data?.taskId;
                if (taskId && onSuccess) {
                    onSuccess(taskId, 'monitoring', response.data);
                } else {
                    onClose?.();
                }
            } else {
                toast.error(response.message || 'Failed to create task.');
            }
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || error.message || 'An unexpected error occurred.');
        },
    });

    useEffect(() => {
        if (taskType === 'monitoring' && monitoringUsage) {
            const includedMetrics = monitoringUsage.metricsUsage?.limitForPeriod || 0;
            const defaultMetrics = getMetricsToShow().slice(0, includedMetrics);
            setSelectedMetrics(defaultMetrics);
        }
    }, [taskType, monitoringUsage]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        let payload: any;
        if (taskType === 'monitoring') {
            if (!isTeamOwner) {
                toast.error('Only team owners can create monitoring tasks.');
                return;
            }
            if (isFreeUser) {
                toast.error('This feature is not available for free users.');
                return;
            }
            if (!urlToMonitor.trim()) {
                toast.error('URL is required for monitoring tasks.');
                return;
            }
            // Block duplicate URL + formFactor combinations
            if (isDuplicateUrl) {
                toast.error(`This URL is already being monitored for ${formFactor}. Choose a different device type or URL.`);
                return;
            }
            if (!validateThresholds(thresholds, getMetricsToShow())) {
                return;
            }
            payload = {
                website_url: urlToMonitor,
                metrics_to_track: selectedMetrics,
                title: title.trim() || urlToMonitor, // Fallback to URL if title is empty
                description: description.trim() || undefined,
                thresholds: thresholds,
                form_factor: formFactor,
            };
        }
        mutation.mutate(payload);
    };

    return (
        <Card className="mt-6 w-full mx-auto">
            <CardHeader>
                <CardTitle>{taskType === 'monitoring' ? 'Add a New URL to Monitoring' : 'Create a New Task'}</CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <Label htmlFor="task-type-select">Task Type</Label>
                        <Select value={taskType} onValueChange={(value) => setTaskType(value as TaskType)}>
                            <SelectTrigger id="task-type-select" className="w-full mt-1">
                                <SelectValue placeholder="Select a task type..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="agent" disabled={isFreeLikeUser}>Agent Task</SelectItem>
                                <SelectItem value="monitoring" disabled={isFreeUser}>Monitoring</SelectItem>
                                <SelectItem value="image_op" disabled={!canCreateImageOpt || isFreeUser}>Image Optimization</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>



                    {taskType === 'monitoring' && (
                        !isTeamOwner ? (
                            <Alert variant="destructive">
                                <AlertTriangleIcon className="h-4 w-4" />
                                <AlertTitle>Permission Denied</AlertTitle>
                                <AlertDescription>
                                    Only team owners can create monitoring tasks. Please ask your team owner to perform this action.
                                </AlertDescription>
                            </Alert>
                        ) : (
                            <>
                                {/* Trial proration notice */}
                                {monitoringUsage?.subscriptionStatus === 'trialing' && (
                                    <Alert className="mb-4">
                                        <AlertTitle>Trial Period</AlertTitle>
                                        <AlertDescription>
                                            You're on a 7-day trial. Some monthly features (like AI prompts and reports) have prorated limits during trial.
                                            Monitoring features (URLs and metrics) have full access as shown below.
                                        </AlertDescription>
                                    </Alert>
                                )}
                                <div>
                                    <Label htmlFor="monitoring-url">URL to Monitor</Label>
                                    <Input id="monitoring-url" type="url" placeholder="https://example.com" value={urlToMonitor} onChange={(e) => setUrlToMonitor(e.target.value)} onBlur={handleUrlBlur} required className="mt-1" />
                                </div>
                                <div>
                                    <Label htmlFor="form-factor-select">Device Type</Label>
                                    <Select value={formFactor} onValueChange={(value) => setFormFactor(value as 'mobile' | 'desktop')}>
                                        <SelectTrigger id="form-factor-select" className="w-full mt-1">
                                            <SelectValue placeholder="Select device type..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="mobile">Mobile</SelectItem>
                                            <SelectItem value="desktop">Desktop</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {isDuplicateUrl && (
                                        <Alert variant="destructive" className="mt-2">
                                            <AlertTitle>Duplicate URL</AlertTitle>
                                            <AlertDescription>
                                                This URL is already being monitored for {formFactor}. You can add it for {formFactor === 'mobile' ? 'desktop' : 'mobile'} instead.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="task-title" className="text-sm font-medium">Task Title *</Label>
                                    <Input id="task-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter a clear, concise title for this task" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="task-description" className="text-sm font-medium">Description</Label>
                                    <Textarea id="task-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Provide more details about what needs to be done..." className="min-h-32 resize-y" maxLength={300} />
                                    <p className="text-xs text-muted-foreground">{300 - (description?.length || 0)} characters remaining.</p>
                                </div>
                                {isUsageLoading ? (
                                    <Skeleton className="h-20 w-full" />
                                ) : isFreeUser ? (
                                    <div className="space-y-3">
                                        <Label>Metrics to Track</Label>
                                        <div className="rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 p-4">
                                            <div className="flex gap-3">
                                                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <h5 className="font-medium text-amber-900 dark:text-amber-100 mb-2">Subscription Required</h5>
                                                    <div className="text-sm text-amber-800 dark:text-amber-200 space-y-2">
                                                        <p>Monitoring features require an active subscription to track performance metrics and set up alerts.</p>
                                                        <p>
                                                            <Link to="/subscriptions" className="font-medium underline hover:text-amber-900 dark:hover:text-amber-50">
                                                                View Subscription Plans
                                                            </Link>
                                                            {' '}to start monitoring your website's performance.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : monitoringUsage && (
                                    <div>
                                        <Label>Metrics to Track</Label>
                                        {(monitoringUsage.metricsUsage?.limitForPeriod || 0) >= 6 ? (
                                            <p className="text-sm text-muted-foreground mt-2">All metrics are included in your plan and will be tracked.</p>
                                        ) : (
                                            <>
                                                <div className="mt-2">
                                                    <p className="text-sm font-medium">Included in your plan:</p>
                                                    <div className="flex flex-wrap gap-4 mt-2">
                                                        {getMetricsToShow().slice(0, monitoringUsage.metricsUsage?.limitForPeriod || 0).map(metric => (
                                                            <div key={metric} className="flex items-center space-x-2">
                                                                <CheckCircle className="h-4 w-4 text-green-600" />
                                                                <Label htmlFor={metric} className="text-sm font-medium uppercase">{metric}</Label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <h4 className="text-lg font-medium pt-4">Alert Thresholds</h4>
                                    <p className="text-sm text-muted-foreground">Set the values that will trigger an alert. (15% deviation from ideal scores is allowed)</p>
                                    <Card className="pt-4">
                                        <CardContent className="space-y-4">
                                            {getMetricsToShow().map(metric => {
                                                const info = metricInfo[metric as keyof typeof metricInfo];
                                                // Get ideal scores based on selected form factor
                                                const formFactorIdealScores = getIdealScoresForFormFactor(formFactor);
                                                const idealScoreRaw = formFactorIdealScores[metric as keyof typeof formFactorIdealScores];
                                                // Convert ideal score to display units (seconds for time-based metrics)
                                                const idealScoreDisplay = idealScoreRaw / info.conversion;
                                                const currentValue = thresholds[metric as keyof typeof thresholds] / info.conversion;
                                                const deviation = idealScoreRaw ? ((currentValue * info.conversion - idealScoreRaw) / idealScoreRaw) * 100 : 0;
                                                const { min, max } = calculateIdealScoreRange(idealScoreRaw, info.conversion);

                                                return (
                                                    <div className="flex items-center space-x-4" key={metric}>
                                                        <Label htmlFor={`${metric}-threshold`} className="w-24 font-medium">{metric.toUpperCase()} {info.unit && <span className="text-xs text-muted-foreground">({info.unit})</span>}</Label>
                                                        <Slider
                                                            id={`${metric}-threshold`}
                                                            min={min}
                                                            max={max}
                                                            step={0.001}
                                                            value={[currentValue]}
                                                            onValueChange={(value) => handleThresholdChange(metric as keyof typeof thresholds, value)}
                                                            className="flex-1"
                                                        />
                                                        <Input
                                                            type="number"
                                                            step="0.001"
                                                            value={metric === 'cls' ? currentValue.toFixed(3) : currentValue.toFixed(2)}
                                                            onChange={e => handleThresholdChange(metric as keyof typeof thresholds, e.target.value)}
                                                            className="w-24"
                                                        />
                                                        <div className="w-24 text-center text-sm">
                                                            <p className={Math.abs(deviation) > 15 ? 'text-red-500' : 'text-green-500'}>{deviation.toFixed(0)}%</p>
                                                            <p className="text-xs text-muted-foreground">Ideal: {metric === 'cls' ? idealScoreDisplay.toFixed(3) : idealScoreDisplay.toFixed(2)}{info.unit}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Credit validation display */}
                                {creditValidation && (
                                    <CostInfoDisplay validationResult={creditValidation} />
                                )}
                            </>
                        )
                    )}

                    {taskType === 'image_op' && (
                        <ImageOptimizationForm
                            onSuccess={(taskId) => {
                                if (onSuccess) {
                                    onSuccess(taskId, 'image_op');
                                } else {
                                    onClose?.();
                                }
                            }}
                            onCancel={onClose}
                        />
                    )}

                    {taskType === 'agent' && (
                        isFreeLikeUser ? (
                            <div className="space-y-4">
                                <Alert className="bg-amber-50 border-amber-300 dark:bg-amber-950/20 dark:border-amber-700">
                                    <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                                    <AlertTitle>Upgrade required</AlertTitle>
                                    <AlertDescription>
                                        Agent Workspace is available on paid plans. Upgrade to continue.
                                    </AlertDescription>
                                </Alert>
                                <div className="flex justify-end gap-2">
                                    {onClose && <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>}
                                    <Button type="button" onClick={() => navigate('/subscriptions')}>
                                        View Plans
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <Alert className="bg-primary/5 border-primary/20 flex flex-row gap-4 items-start">
                                    <Bot className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                                    <div className="flex-1">
                                        <AlertTitle className="text-primary mb-1">Agent Workspace</AlertTitle>
                                        <AlertDescription>
                                            Agent tasks allow you to assign AI developers to work directly on your GitHub repositories.
                                            You will be redirected to the Agent Workspace to select a repository and branch.
                                        </AlertDescription>
                                    </div>
                                </Alert>

                                <div className="space-y-2">
                                    <Label htmlFor="agent-prompt" className="text-sm font-medium">What should the agent do?</Label>
                                    <Textarea
                                        id="agent-prompt"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="e.g. Fix the memory leak in the dashboard component..."
                                        className="min-h-32 resize-y"
                                    />
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    {onClose && <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>}
                                    <Button
                                        type="button"
                                        onClick={() => navigate('/agent', { state: { prompt: description, autoStart: false } })}
                                        disabled={!description.trim()}
                                        className="gap-2"
                                    >
                                        Continue to Workspace
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )
                    )}

                    {taskType !== 'image_op' && taskType !== 'agent' && (
                        <div className="flex justify-end gap-2 pt-4">
                            {onClose && <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>}
                            <Button type="submit" disabled={mutation.isPending || (taskType === 'monitoring' && (isUsageLoading || !isTeamOwner || isFreeUser || !creditValidation?.canAfford))}>
                                {mutation.isPending ? "Creating..." : "Create Task"}
                            </Button>
                        </div>
                    )}
                </form>
            </CardContent>
        </Card >
    );
};
